import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import EmojiSymbolsIcon from "@mui/icons-material/EmojiSymbols";
import {
  Box,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  SxProps,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";

import dayjs, { Dayjs } from "dayjs";

import TextField from "@/components/TextField";
import EmotionPicker from "@/components/TextField/EmoticonPicker";
import emoticons from "@/components/TextField/emoticons";

import {
  DEBOUNCE,
  Editable,
  Item,
} from "@/extensions/obituary/pages/detail/Story/shared";
import { Emoticon } from "@/extensions/obituary/types";

import { useStory } from "../context";

import EditableItem from "./EditableItem";

import { Nullish, Option } from "@/utils/types";

export interface Date extends Item {
  type: "date";
  variant: "birth-date" | "deceased-date";
  date: string;
  icon?: string;
  prefix?: string;
  suffix?: string;
  wrap?: boolean;
}

export function isDate(item: Item): item is Date {
  return item.type === "date";
}

export const variants: Option[] = [
  { value: "birth-date", label: "Född" },
  { value: "deceased-date", label: "Avliden" },
];

export function isVariant(value: unknown): value is Date["variant"] {
  return variants.some((option) => option.value === value);
}

const styles: Record<string, SxProps> = {
  input: {
    bgcolor: "background.paper",
    width: "100%",
  },
};

export const Edit: Editable<Date> = ({
  item: { date: initialDate, hidden, icon, variant, prefix, suffix, wrap },
  path,
}) => {
  const { change } = useStory();

  const [date, setDate] = useState(dayjs(initialDate));
  const [openEmoticonPicker, setOpenEmoticonPicker] = useState(false);
  const emoticonButtonRef = useRef<HTMLButtonElement>(null);
  const [selectedIcon, setSelectedIconState] = useState<Emoticon | null>(
    () => emoticons.find((e) => e.code === icon) ?? null,
  );

  useEffect(() => {
    setSelectedIconState(emoticons.find((e) => e.code === icon) ?? null);
  }, [icon]);

  const handleChangeDate = useCallback(
    (value: Nullish<Dayjs>) => {
      if (value == null) return;
      setDate(value);
      change<Date>(path, { date: value.format("YYYY-MM-DD") });
    },
    [change, path],
  );

  const handleChangeData = useCallback(
    (key: keyof Date) => (value: string) =>
      change<Date>(path, { [key]: value }),
    [change, path],
  );

  const handleSetSelectedIcon = useCallback(
    (newIcon: Emoticon | null) => {
      setSelectedIconState(newIcon);
      change<Date>(path, { icon: newIcon?.code ?? undefined });
    },
    [change, path],
  );

  const handleChangeWrap = useCallback(
    (_: React.SyntheticEvent, checked: boolean) =>
      change<Date>(path, { wrap: checked }),
    [change, path],
  );

  const handleSelectDateIcon = useCallback(
    (selected: Emoticon) => {
      console.log(selected);
      handleSetSelectedIcon(selected);
      setOpenEmoticonPicker(false);
    },
    [handleSetSelectedIcon],
  );

  const title = useMemo(() => {
    let title = variants.find((option) => option.value === variant)?.label;
    if (hidden) title = `${title} (Dold)`;
    return title ?? "Datum";
  }, [hidden, variant]);

  return (
    <EditableItem hidden={hidden} path={path} title={title}>
      {!hidden && (
        <Stack gap={2}>
          <TextField
            debounce={DEBOUNCE}
            emoticons={emoticons}
            emoticonsButtonSx={{
              bottom: -1,
              right: 0,
            }}
            label="Prefix"
            name="prefix"
            size="small"
            sx={styles.input}
            value={prefix}
            variant="outlined"
            onChange={handleChangeData("prefix")}
          />

          <Box sx={{ position: "relative" }}>
            <DatePicker
              format="YYYY-MM-DD"
              label="Datum"
              slotProps={{
                textField: {
                  fullWidth: true,
                  sx: {
                    ...styles.input,
                    "& .MuiOutlinedInput-input": { pl: 6.5 },
                  },
                },
              }}
              value={date}
              onChange={handleChangeDate}
            />

            <IconButton
              ref={emoticonButtonRef}
              disableRipple
              sx={{
                position: "absolute",
                left: 6,
                bottom: 6,
                bgcolor: selectedIcon ? "common.white" : "background.paper",
                border: "1px solid",
                borderColor: "grey.400",
                borderRadius: "50%",
                transform: "scale(0.7)",
                zIndex: 1,
                "&:hover": { borderColor: "black" },
              }}
              tabIndex={-1}
              onClick={() => setOpenEmoticonPicker((prev) => !prev)}
            >
              {selectedIcon ? (
                <Box
                  alt={selectedIcon.name}
                  component="img"
                  src={`/emoticons/${selectedIcon.image}`}
                  sx={{
                    height: 24,
                    width: "auto",
                    display: "block",
                    transform: "scale(1.2)",
                  }}
                />
              ) : (
                <EmojiSymbolsIcon />
              )}
            </IconButton>

            <EmotionPicker
              anchor={emoticonButtonRef}
              emoticons={emoticons}
              open={openEmoticonPicker}
              selectedIcon={selectedIcon}
              setSelectedIcon={handleSetSelectedIcon}
              onClickAway={() => setOpenEmoticonPicker(false)}
              onEmoticonClick={handleSelectDateIcon}
            />
          </Box>

          <TextField
            debounce={DEBOUNCE}
            label="Suffix"
            name="suffix"
            size="small"
            sx={styles.input}
            value={suffix}
            variant="outlined"
            onChange={handleChangeData("suffix")}
          />

          <FormControlLabel
            control={
              <Switch
                checked={Boolean(wrap)}
                inputProps={{ "aria-label": "Datum på egen rad" }}
                onChange={handleChangeWrap}
              />
            }
            label="Datum på egen rad"
            sx={{ ml: 0, mt: -1 }}
          />
        </Stack>
      )}
    </EditableItem>
  );
};

export default memo(Edit);
